
import React, { useState, useEffect } from 'react';
import RegistrationForm from './components/RegistrationForm';
import Dashboard from './components/Dashboard';
import EventSchedule from './components/EventSchedule';
import PublicRoster from './components/PublicRoster';
import MyRegistration from './components/MyRegistration';
import OutlookRecordSubmit from './components/OutlookRecordSubmit';
import InventoryManager from './components/InventoryManager';
import Toast, { ToastMessage, ToastType } from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import { HistoricalRecord, InventoryCodeSession, Registrant, EventOption, WatchlistItem, TripParticipant } from './types';
import { LayoutDashboard, LogOut, AlertTriangle, KeyRound, Loader2, Users, UserCog, ShieldCheck, Search, Package } from 'lucide-react';
import * as firebaseService from './services/firebaseService';
import {
  forgetInventoryCodeUserName,
  readInventoryCodeUserName,
  redeemInventoryAccessCode,
  restoreInventoryCodeSession,
} from './services/inventoryAccessService';
import { sendConfirmationEmail } from './services/emailService';
import { User, getRedirectResult, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from "./firebase";
import { normalizeEventDateRange } from './utils/dateUtils';

// --- 超級管理員 (Super Admins) ---
const SUPER_ADMINS = [
  "junzhi1117@gmail.com",
  "henryhsu7@gmail.com"
];

type View = 'home' | 'form' | 'public-roster' | 'my-registration' | 'admin-login' | 'admin-dashboard' | 'inventory-management' | 'inventory-access' | 'outlook-record-submit';

const parseRegistrationYear = (value: string | null): number | null => {
  if (!value || !/^\d{4}$/.test(value)) return null;
  const year = Number(value);
  return year >= 2000 && year <= 2100 ? year : null;
};

const isValidRegistrationYear = (value: number | undefined): value is number => (
  typeof value === 'number' && Number.isInteger(value) && value >= 2000 && value <= 2100
);

const parseHashRoute = (): {
  view: View;
  outlookRecordId: string | null;
  eventId: string | null;
  registrationYear: number | null;
} => {
  if (typeof window === 'undefined') {
    return { view: 'home', outlookRecordId: null, eventId: null, registrationYear: null };
  }

  const hash = window.location.hash.replace(/^#/, '');
  if (hash.startsWith('outlook-record-submit/')) {
    return {
      view: 'outlook-record-submit',
      outlookRecordId: decodeURIComponent(hash.replace('outlook-record-submit/', '')),
      eventId: null,
      registrationYear: null,
    };
  }

  // QR code 深層連結：#register/<eventId> 或 #register?event=<eventId>；也支援 ?year=YYYY。
  if (hash === 'register' || hash.startsWith('register/') || hash.startsWith('register?')) {
    const rest = hash.slice('register'.length);
    const queryStart = rest.indexOf('?');
    const pathPart = queryStart >= 0 ? rest.slice(0, queryStart) : rest;
    const queryParams = new URLSearchParams(queryStart >= 0 ? rest.slice(queryStart + 1) : '');
    const rawEventId = rest.startsWith('/')
      ? pathPart.slice(1).split(/[?#]/)[0]
      : queryParams.get('event') || '';
    return {
      view: 'form',
      outlookRecordId: null,
      eventId: rawEventId ? decodeURIComponent(rawEventId) : null,
      registrationYear: parseRegistrationYear(queryParams.get('year')),
    };
  }

  const knownViews: View[] = ['home', 'form', 'public-roster', 'my-registration', 'admin-login', 'admin-dashboard', 'inventory-management', 'inventory-access'];
  if (knownViews.includes(hash as View)) {
    return {
      view: hash as View,
      outlookRecordId: null,
      eventId: null,
      registrationYear: null,
    };
  }

  const submitPathMatch = window.location.pathname.match(/^\/outlook-records\/submit\/([^/?#]+)/);
  if (submitPathMatch) {
    return {
      view: 'outlook-record-submit',
      outlookRecordId: decodeURIComponent(submitPathMatch[1]),
      eventId: null,
      registrationYear: null,
    };
  }

  return {
    view: 'home',
    outlookRecordId: null,
    eventId: null,
    registrationYear: null,
  };
};

type FormRouteOptions = {
  eventId?: string;
  year?: number;
};

const appViewUrl = (view: View, routeOptions?: FormRouteOptions) => {
  if (view === 'form') {
    if (routeOptions?.eventId) {
      return `/#register/${encodeURIComponent(routeOptions.eventId)}`;
    }
    if (routeOptions?.year !== undefined) {
      return `/#register?year=${routeOptions.year}`;
    }
  }

  return `/#${view}`;
};

const ChristianCross = ({ className, size = 24 }: { className?: string, size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 2v20M8 8h8" />
  </svg>
);

const getUserInitials = (currentUser: User | null): string => {
  const source = currentUser?.displayName || currentUser?.email || '管理';
  const normalized = source.trim();
  if (!normalized) return '管';

  const nameParts = normalized
    .replace(/@.*$/, '')
    .split(/\s+/)
    .filter(Boolean);

  if (nameParts.length >= 2) {
    return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
  }

  return normalized.slice(0, 2).toUpperCase();
};

const App: React.FC = () => {
  const initialRoute = parseHashRoute();
  const [currentView, setCurrentView] = useState<View>(initialRoute.view);
  const [outlookSubmitRecordId, setOutlookSubmitRecordId] = useState<string | null>(initialRoute.outlookRecordId);
  const [user, setUser] = useState<User | null>(null);
  // SECURITY: Default isAdmin to false for production
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasInventoryAccess, setHasInventoryAccess] = useState(false);
  // 現場清點同工用梯次通行碼換到的臨時身分（匿名登入 + custom claims）
  const [inventoryCodeSession, setInventoryCodeSession] = useState<InventoryCodeSession | null>(null);
  const [accessCodeInput, setAccessCodeInput] = useState('');
  const [accessCodeName, setAccessCodeName] = useState(readInventoryCodeUserName());
  const [isRedeemingCode, setIsRedeemingCode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [hasLoadedPublicData, setHasLoadedPublicData] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [toast, setToast] = useState<ToastMessage | null>(null);

  const [historicalData, setHistoricalData] = useState<HistoricalRecord[]>([]);
  const [tripParticipants, setTripParticipants] = useState<TripParticipant[]>([]);
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [registrations, setRegistrations] = useState<Registrant[]>([]);
  const [publicRegistrations, setPublicRegistrations] = useState<Registrant[]>([]);

  // Global Data
  const [allEvents, setAllEvents] = useState<EventOption[]>([]);
  const [systemYear, setSystemYear] = useState<number>(new Date().getFullYear());

  // Pre-selected event for registration flow optimization
  const [preSelectedEventId, setPreSelectedEventId] = useState<string | undefined>(initialRoute.eventId ?? undefined);
  const [registrationYear, setRegistrationYear] = useState<number | undefined>(initialRoute.registrationYear ?? undefined);

  // Navigation history management
  const [navigationHistory, setNavigationHistory] = useState<View[]>(['home']);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);

  // Suppress TS6133 warning - navigationHistory is maintained for browser back/forward functionality
  // while the actual navigation state is tracked through setNavigationHistory
  void navigationHistory; // Mark as intentionally unused but necessary

  const showToast = (type: ToastType, title: string, message?: string) => {
    setToast({ id: Date.now().toString(), type, title, message });
  };

  // Enhanced navigation function with history tracking
  const navigateTo = (view: View, routeOptions?: FormRouteOptions) => {
    if (!isNavigatingBack) {
      // Push current view to history before navigating
      setNavigationHistory(prev => [...prev, view]);
      // Update browser history
      window.history.pushState({ view, ...routeOptions }, '', appViewUrl(view, routeOptions));
    }
    if (view !== 'outlook-record-submit') {
      setOutlookSubmitRecordId(null);
    }
    if (view !== 'form') {
      setPreSelectedEventId(undefined);
      setRegistrationYear(undefined);
    }
    setCurrentView(view);
    setIsNavigatingBack(false);
  };

  // Handle browser back button
  useEffect(() => {
    const syncHashRoute = () => {
      const route = parseHashRoute();
      if (route.view === 'outlook-record-submit') {
        setOutlookSubmitRecordId(route.outlookRecordId);
        setCurrentView(route.view);
        return true;
      }
      if (route.view === 'form') {
        // #register/<eventId>：找不到或已額滿的梯次會由報名表自行忽略，直接進一般報名流程
        setOutlookSubmitRecordId(null);
        setPreSelectedEventId(route.eventId ?? undefined);
        setRegistrationYear(route.registrationYear ?? undefined);
        setCurrentView('form');
        return true;
      }
      return false;
    };

    const handlePopState = () => {
      // Note: popstate event cannot be prevented, so preventDefault() is not applicable
      if (syncHashRoute()) return;

      setNavigationHistory(prev => {
        if (prev.length > 1) {
          // Remove current view and go back to previous
          const newHistory = prev.slice(0, -1);
          const previousView = newHistory[newHistory.length - 1];

          setIsNavigatingBack(true);
          setOutlookSubmitRecordId(null);
          setCurrentView(previousView);

          // Clear pre-selection when navigating back
          if (previousView !== 'form') {
            setPreSelectedEventId(undefined);
            setRegistrationYear(undefined);
          }

          return newHistory;
        } else {
          // 🔧 FIX: Stay on home and add a sentinel history entry to prevent app exit
          setCurrentView('home');
          setOutlookSubmitRecordId(null);
          setPreSelectedEventId(undefined);
          setRegistrationYear(undefined);

          // Re-push home state to prevent exiting the app when pressing back on home screen
          window.history.pushState({ view: 'home' }, '', appViewUrl('home'));

          return ['home'];
        }
      });
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', syncHashRoute);

    // 🔧 FIX: Initialize with a sentinel history entry to prevent app exit on first back press
    const routeFromHash = parseHashRoute();
    const isDeepLink = syncHashRoute() || routeFromHash.view !== 'home';
    if (!window.history.state && !isDeepLink) {
      // Create a "bottom" sentinel entry that user can't go past
      window.history.replaceState({ view: 'sentinel' }, '', '/#');
      // Then push the actual home state on top
      window.history.pushState({ view: 'home' }, '', appViewUrl('home'));
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', syncHashRoute);
    };
  }, []);

  const checkIsAdmin = async (email: string | null | undefined): Promise<boolean> => {
    if (!email) return false;
    const normalizedEmail = email.trim().toLowerCase();

    // 1. Check Hardcoded Super Admins
    if (SUPER_ADMINS.includes(normalizedEmail)) return true;

    // 2. Check Firestore Whitelist
    try {
      const allowedAdmins = await firebaseService.getAllowedAdmins();
      return allowedAdmins.map(adminEmail => adminEmail.trim().toLowerCase()).includes(normalizedEmail);
    } catch (error) {
      console.error("Error checking admin permission:", error);
      return false;
    }
  };

  const checkHasInventoryAccess = async (email: string | null | undefined, adminStatus = false): Promise<boolean> => {
    if (!email) return false;
    if (adminStatus) return true;
    const normalizedEmail = email.trim().toLowerCase();

    try {
      const allowedInventoryUsers = await firebaseService.getAllowedInventoryUsers();
      return allowedInventoryUsers.map(inventoryEmail => inventoryEmail.trim().toLowerCase()).includes(normalizedEmail);
    } catch (error) {
      console.error("Error checking inventory permission:", error);
      return false;
    }
  };

  // 1. Auth State Listener
  useEffect(() => {
    getRedirectResult(auth).catch((error) => {
      console.error("Redirect login failed:", error);
      showToast('error', 'Google 登入未完成', '請重新整理後再登入一次；若從 LINE 或 Facebook 開啟，請改用 Safari 或 Chrome。');
    });

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser?.isAnonymous) {
        // 通行碼身分：權限完全來自 ID token 的 claims，重新整理後直接還原。
        setIsAdmin(false);
        setHasInventoryAccess(false);
        setInventoryCodeSession(await restoreInventoryCodeSession(currentUser));
      } else if (currentUser) {
        // Verify admin status on load
        const adminStatus = await checkIsAdmin(currentUser.email);
        const inventoryStatus = await checkHasInventoryAccess(currentUser.email, adminStatus);
        setIsAdmin(adminStatus);
        setHasInventoryAccess(inventoryStatus);
        setInventoryCodeSession(null);
      } else {
        setIsAdmin(false);
        setHasInventoryAccess(false);
        setInventoryCodeSession(null);
      }

      setAuthChecking(false);
    });
    return () => unsubscribe();
  }, []);

  // 2. Initial Public Data Fetch (System Year & Events)
  // 🚀 OPTIMIZED: Only load essential public data (reduced from ~5611 to ~11 reads per visitor)
  useEffect(() => {
    const fetchPublicData = async () => {
      setIsLoading(true);
      try {
        // ✅ Only fetch truly public data (System Year + Events)
        // ❌ Removed: historicalData (3000 reads) and watchlist (100 reads)
        //    These are now loaded only when admin logs in (see useEffect below)
        const [activeYear, evtData] = await Promise.all([
          // 1. System Year
          firebaseService.getSystemYear().catch(e => {
            console.warn("Failed to fetch system year", e);
            return new Date().getFullYear();
          }),

          // 2. Events (Public)
          firebaseService.getEvents().catch(e => {
            console.warn("Failed to fetch events from Firestore (using static data):", e);
            return [];
          })
        ]);

        // Update state with fetched data
        setSystemYear(activeYear);

        setAllEvents(evtData.map(normalizeEventDateRange));

      } catch (error) {
        console.error("Error fetching public data:", error);
        setAllEvents([]);
      } finally {
        setHasLoadedPublicData(true);
        setIsLoading(false);
      }
    };

    fetchPublicData();
  }, []);

  // 2.1. Real-time Sync for Public Roster (只在需要時監聽)
  useEffect(() => {
    if (authChecking) return;

    // ✅ 只在「公開名單」或「後台」頁面才訂閱實時更新
    // 避免在「首頁」、「報名表單」等頁面產生不必要的讀取
    const shouldSubscribe = currentView === 'public-roster' || currentView === 'admin-dashboard';

    if (!shouldSubscribe) return;

    // ✅ 訂閱公開名單實時更新
    const unsubscribe = firebaseService.subscribeToPublicRoster(
      (data) => {
        setPublicRegistrations(data);
      },
      (error) => {
        console.error("Failed to sync public roster:", error);
        showToast('error', '同步失敗', '公開名單無法即時更新，請重新整理頁面。');
      }
    );

    // 清理函數：離開頁面或登出時停止監聽
    return () => {
      unsubscribe();
    };
  }, [authChecking, currentView]);

  // 3. Real-time Sync for Admin Data (管理員專用)
  useEffect(() => {
    if (!isAdmin || !user) return;

    setIsLoading(true);

    // ✅ 訂閱報名資料實時更新
    const unsubscribe = firebaseService.subscribeToRegistrations(
      (data) => {
        setRegistrations(data);
        setIsLoading(false);
      },
      (error) => {
        console.error("Admin data sync failed:", error);
        showToast('error', '同步失敗', '無法即時更新後台資料，請重新整理頁面。');
        setIsLoading(false);
      }
    );

    // ✅ 載入管理員專用資料（historicalData + watchlist）
    // 只在管理員登入時載入，避免一般訪客產生不必要的讀取
    Promise.all([
      firebaseService.getHistoricalData(),
      firebaseService.getWatchlist()
    ]).then(([histData, watchlistData]) => {
      setHistoricalData(histData);
      setWatchlist(watchlistData);
    }).catch(err => {
      console.error("Failed to load admin-only data:", err);
    });

    // 過往出隊名單（第一次參與判斷用）：獨立載入，失敗不影響其他後台資料
    firebaseService.getTripParticipants()
      .then(setTripParticipants)
      .catch(err => {
        console.error("Failed to load trip participants:", err);
        setTripParticipants([]);
      });

    // 清理函數：登出或離開後台時停止監聽
    return () => {
      unsubscribe();
    };
  }, [isAdmin, user]);

  // --- Derived State: Filter events by active System Year ---
  const activeEvents = allEvents.filter(e => (e.year || 2025) === systemYear);
  const preSelectedEvent = preSelectedEventId
    ? allEvents.find(event => event.id === preSelectedEventId)
    : undefined;
  const formYear = preSelectedEvent?.year ?? registrationYear ?? systemYear;
  const formEvents = allEvents.filter(e => (e.year || 2025) === formYear);
  const isInitialHomeDataLoading = currentView === 'home' && !hasLoadedPublicData;
  const showGlobalLoadingOverlay = isLoading && !isInitialHomeDataLoading;
  const currentUserLabel = inventoryCodeSession?.name
    || user?.displayName
    || user?.email
    || '物資人員';
  // 通行碼身分沒有 Google 帳號，但一樣要能進物資管理。
  const canOpenInventory = hasInventoryAccess || inventoryCodeSession !== null;

  const handleNewRegistration = async (data: Omit<Registrant, 'id' | 'submissionDate'>) => {
    setIsLoading(true);
    try {
      const addedReg = await firebaseService.submitRegistration(data);

      // ✅ 立即更新前端狀態，確保公開名單即時顯示（不依賴實時同步延遲）
      if (addedReg.isApproved) {
        setPublicRegistrations(prev => [addedReg, ...prev]);
      }
      // Only update admin list if we are currently admin
      if (isAdmin && registrations.length > 0) {
        setRegistrations(prev => [addedReg, ...prev]);
      }

      // ✉️ 異步寄發報名確認信（不阻塞用戶體驗）
      sendConfirmationEmail(addedReg, allEvents).catch(emailError => {
        console.error('寄送確認信時發生錯誤:', emailError);
        // 不影響報名流程，只記錄錯誤
      });

      // 4. Show Feedback - 使用 Toast 通知代替阻塞式 alert
      if (addedReg.verificationResult?.matchType === 'WATCHLIST') {
        showToast('info', '報名已送出，等待審核', '請留意信箱及後續通知。');
      } else if (addedReg.isApproved) {
        showToast('success', '報名已核准', '確認信已寄送，可至「分工名單」查看。');
      } else {
        showToast('info', '報名已送出', '請留意信箱及後續遴選通知。');
      }

      navigateTo('home');
      setPreSelectedEventId(undefined); // Clear pre-selection after successful submission
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Error submitting registration:", error);
      const errorCode = (error as { code?: string })?.code;
      if (errorCode === 'functions/already-exists' || errorCode === 'already-exists') {
        showToast(
          'info',
          '已報名過此梯隊',
          '系統發現相同 Email 與電話已報名過你選擇的梯隊。請使用「查詢我的報名」確認，若要變更請先取消原報名。'
        );
      } else {
        showToast('error', '報名失敗', '系統發生錯誤，請稍後再試。');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateHistorical = async (newData: HistoricalRecord[]) => {
    setIsLoading(true);
    try {
      // ✅ 區分「清空」和「匯入」兩種情況
      if (newData.length === 0) {
        // 清空：直接更新 State，不需要重新讀取 Firestore
        // （Firestore 已經在 clearAllHistoricalData 中刪除完畢）
        setHistoricalData([]);
        showToast('success', '清空成功', '歷史資料已全部刪除。');
      } else {
        // 匯入：儲存後重新讀取，確保資料同步
        await firebaseService.saveHistoricalBatch(newData);
        const refreshedData = await firebaseService.getHistoricalData();
        setHistoricalData(refreshedData);
        showToast('success', '匯入成功', `成功新增 ${newData.length} 筆歷史資料。`);
      }
    } catch (error) {
      console.error("Error updating historical data:", error);
      const errorMsg = newData.length === 0 ? '清空失敗' : '匯入失敗';
      const errorDetail = newData.length === 0 ? '請稍後再試。' : '請檢查 CSV 格式是否正確。';
      showToast('error', errorMsg, errorDetail);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateWatchlist = (newData: WatchlistItem[]) => {
    setWatchlist(newData);
  };

  const handleAddEvents = async (newEvents: EventOption[]) => {
    setIsLoading(true);
    try {
      // Update local state immediately for better UX
      // Merge with existing events that are NOT in the current year (keep history)
      const otherYearEvents = allEvents.filter(e => (e.year || 2025) !== systemYear);
      const updatedAllEvents = [...otherYearEvents, ...newEvents];


      // Sync with Firebase
      // 1. Add/Update new ones
      for (const ev of newEvents) {
        // Check if it really changed or is new to save writes? For now just write.
        await firebaseService.addEvent(ev);
      }
      // 2. Delete removed ones (only from current year view)
      const currentYearIds = new Set(newEvents.map(e => e.id));
      const idsToDelete = activeEvents.filter(e => !currentYearIds.has(e.id)).map(e => e.id);

      for (const id of idsToDelete) {
        await firebaseService.deleteEvent(id);
      }

      setAllEvents(updatedAllEvents);
      showToast('success', '更新成功', '梯隊資料已更新。');
    } catch (error) {
      console.error("Error updating events:", error);
      showToast('error', '更新失敗', '無法更新梯隊資料。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditEvent = async (updatedEvent: EventOption) => {
    setIsLoading(true);
    try {
      const normalizedEvent = normalizeEventDateRange(updatedEvent);
      await firebaseService.updateEvent(normalizedEvent);
      setAllEvents(prev => prev.map(ev => ev.id === normalizedEvent.id ? normalizedEvent : ev));
      showToast('success', '更新成功', '梯隊資料已修改。');
    } catch (error) {
      console.error("Error updating event:", error);
      showToast('error', '更新失敗', '無法修改資料。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEventAndRegistrants = async (
    updatedEvent: EventOption,
    updatedRegistrants: Registrant[],
    successTitle: string,
    successMessage: string
  ) => {
    setIsLoading(true);
    try {
      const normalizedEvent = normalizeEventDateRange(updatedEvent);
      await firebaseService.updateEventAndRegistrants(normalizedEvent, updatedRegistrants);

      setAllEvents(prev => prev.map(ev => ev.id === normalizedEvent.id ? normalizedEvent : ev));

      if (updatedRegistrants.length > 0) {
        const updatedMap = new Map(updatedRegistrants.map(reg => [reg.id, reg]));
        setRegistrations(prev => prev.map(reg => updatedMap.get(reg.id) || reg));
        setPublicRegistrations(prev => {
          const next = prev
            .map(reg => updatedMap.get(reg.id) || reg)
            .filter(reg => reg.isApproved);

          updatedRegistrants.forEach(reg => {
            if (reg.isApproved && !next.some(existing => existing.id === reg.id)) {
              next.push(reg);
            }
          });

          return next;
        });
      }

      showToast('success', successTitle, successMessage);
    } catch (error) {
      console.error("Error updating event and assignments:", error);
      showToast('error', '更新失敗', '無法儲存批次更新，請檢查網路連線。');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRegistrantToLocalState = (registrant: Registrant) => {
    setRegistrations(prev => {
      if (prev.some(existing => existing.id === registrant.id)) return prev;
      return [registrant, ...prev];
    });

    if (registrant.isApproved) {
      setPublicRegistrations(prev => {
        if (prev.some(existing => existing.id === registrant.id)) return prev;
        return [registrant, ...prev];
      });
    }
  };

  const handleCreateRegistrant = async (data: Omit<Registrant, 'id'>): Promise<Registrant> => {
    const registrant = await firebaseService.addRegistration(data);
    handleAddRegistrantToLocalState(registrant);
    return registrant;
  };

  const handleUpdateRegistrant = async (updatedRegistrant: Registrant) => {
    try {
      const original = registrations.find(r => r.id === updatedRegistrant.id);
      setRegistrations(prev => prev.map(r => r.id === updatedRegistrant.id ? updatedRegistrant : r));

      if (updatedRegistrant.isApproved) {
        setPublicRegistrations(prev => {
          const exists = prev.find(p => p.id === updatedRegistrant.id);
          if (exists) return prev.map(p => p.id === updatedRegistrant.id ? updatedRegistrant : p);
          return [updatedRegistrant, ...prev];
        });
      } else {
        setPublicRegistrations(prev => prev.filter(p => p.id !== updatedRegistrant.id));
      }

      await firebaseService.updateRegistrant(updatedRegistrant);
      showToast('success', '已更新', `${updatedRegistrant.name} 的資料已儲存。`);

      if ((!original?.isApproved) && updatedRegistrant.isApproved) {
        sendConfirmationEmail(updatedRegistrant, allEvents).then(success => {
          if (success) showToast('info', '信件已發送', `審核通過確認信已寄送給 ${updatedRegistrant.name}`);
        });
      }
    } catch (error) {
      console.error("Error updating registrant:", error);
      showToast('error', '更新失敗', '無法儲存變更，請檢查網路連線。');
    }
  };

  const handleUpdateSystemYear = async (newYear: number) => {
    if (window.confirm(`確定要將系統切換到 ${newYear} 年度嗎？\n\n1. 首頁標題將變更為 ${newYear}。\n2. 梯隊列表將只顯示 ${newYear} 的梯隊。\n3. 舊年度資料將被隱藏但不會刪除。`)) {
      setIsLoading(true);
      try {
        await firebaseService.updateSystemYear(newYear);
        setSystemYear(newYear);
        showToast('success', '年度已切換', `目前系統年度：${newYear}`);
      } catch (e) {
        console.error(e);
        showToast('error', '切換失敗', '無法更新系統年度');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleGoogleLogin = async () => {
    setLoginError('');
    try {
      // Clear any existing auth state to ensure fresh login
      if (auth.currentUser && !isAdmin && !hasInventoryAccess) {
        await signOut(auth);
      }

      const result = await signInWithPopup(auth, googleProvider);
      const email = result.user?.email;

      if (email) {
        const adminStatus = await checkIsAdmin(email);
        const inventoryStatus = await checkHasInventoryAccess(email, adminStatus);

        if (adminStatus) {
          setIsAdmin(true);
          setHasInventoryAccess(true);
          navigateTo('admin-dashboard');
          showToast('success', '歡迎回來', '管理員登入成功。');
        } else if (inventoryStatus) {
          setIsAdmin(false);
          setHasInventoryAccess(true);
          navigateTo('inventory-management');
          showToast('success', '登入成功', '可開始更新物資庫存。');
        } else {
          setIsAdmin(false);
          setHasInventoryAccess(false);
          await signOut(auth);
          setLoginError('您的帳號沒有後台或物資管理權限。請聯繫系統管理員。');
        }
      }
    } catch (error: any) {
      console.error("Login failed:", error);

      // Provide detailed error messages
      let errorMessage = '登入失敗，請重試。';

      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = '登入視窗已關閉，請重新嘗試。';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = '登入視窗被瀏覽器阻擋，請允許彈出視窗。';
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = '已取消登入，請重新嘗試。';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = '網路連線失敗，請檢查網路連線。';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = '登入嘗試次數過多，請稍後再試。';
      } else if (error.code === 'auth/unauthorized-domain') {
        const currentHost = window.location.host;
        errorMessage = window.location.hostname === '127.0.0.1'
          ? '目前使用 127.0.0.1 開啟本機站，Firebase Auth 只授權 localhost。請改用 http://localhost:5276 後重新登入。'
          : `目前網域 ${currentHost} 未加入 Firebase Auth 授權網域，請到 Firebase Console > Authentication > Settings > Authorized domains 加入此網域。`;
      } else if (error.message) {
        errorMessage = `登入失敗：${error.message}`;
      }

      setLoginError(errorMessage);
    }
  };

  const handleRedeemInventoryCode = async () => {
    const trimmedName = accessCodeName.trim();
    const trimmedCode = accessCodeInput.trim();

    if (!trimmedName || !trimmedCode) {
      setLoginError('請填寫姓名與通行碼。');
      return;
    }

    setLoginError('');
    setIsRedeemingCode(true);

    try {
      const session = await redeemInventoryAccessCode(trimmedCode, trimmedName);
      setInventoryCodeSession(session);
      setAccessCodeInput('');
      navigateTo('inventory-management');
      showToast('success', '通行成功', '可開始清點本梯物資。');
    } catch (error: any) {
      console.error('Inventory code redemption failed:', error);

      if (error?.code === 'functions/resource-exhausted') {
        setLoginError('輸入錯誤次數過多，請稍後再試。');
      } else if (error?.code === 'functions/permission-denied') {
        setLoginError('通行碼錯誤或已失效，請向負責人確認。');
      } else {
        setLoginError('無法驗證通行碼，請確認網路連線後再試一次。');
      }
    } finally {
      setIsRedeemingCode(false);
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
    forgetInventoryCodeUserName();
    setUser(null);
    setIsAdmin(false);
    setHasInventoryAccess(false);
    setInventoryCodeSession(null);
    setAccessCodeName('');
    navigateTo('home');
    showToast('info', '已登出', '您已登出目前的身分。');
  };

  const handleRegisterClick = (eventId?: string, year?: number) => {
    const selectedEvent = eventId
      ? allEvents.find(event => event.id === eventId)
      : undefined;
    const targetYear = selectedEvent?.year ?? (isValidRegistrationYear(year) ? year : undefined);

    setPreSelectedEventId(eventId);
    setRegistrationYear(targetYear);
    navigateTo(
      'form',
      eventId
        ? { eventId }
        : targetYear !== undefined
        ? { year: targetYear }
        : undefined
    );
    window.scrollTo(0, 0);
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="animate-spin text-blue-600 h-12 w-12 mx-auto mb-4" />
          <p className="text-gray-500 font-bold">系統載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen flex flex-col bg-gray-50 font-sans text-gray-900">
        <Toast toast={toast} onClose={() => setToast(null)} />

      {/* Navigation Bar */}
      <nav className="bg-white/90 backdrop-blur-md text-blue-900 shadow-sm border-b border-gray-200 transition-all">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-2 sm:h-20">
            <div className="flex min-w-0 shrink items-center cursor-pointer group" onClick={() => {
              navigateTo('home');
              setPreSelectedEventId(undefined);
            }}>
              <div className="mr-2 shrink-0 rounded-xl bg-blue-700 p-2 text-white shadow-sm ring-2 ring-blue-50 transition-all duration-300 group-hover:scale-105 group-hover:bg-blue-800 group-hover:ring-blue-100 sm:mr-3">
                <ChristianCross className="text-white" size={24} />
              </div>
              <div className="min-w-0">
                <span className="block truncate whitespace-nowrap text-base font-extrabold leading-tight tracking-tight transition-colors group-hover:text-blue-700 sm:text-xl">TJC 醫宣</span>
                <span className="hidden text-xs font-bold tracking-widest text-gray-500 sm:block">MED MISSION</span>
              </div>
            </div>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex space-x-1 lg:space-x-4">
              <button
                onClick={() => {
                  navigateTo('home');
                  setPreSelectedEventId(undefined);
                }}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${currentView === 'home' || currentView === 'form' ? 'text-blue-800 bg-blue-50 shadow-inner' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
              >
                梯隊報名
              </button>
              <button
                onClick={() => navigateTo('public-roster')}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${currentView === 'public-roster' ? 'text-blue-800 bg-blue-50 shadow-inner' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
              >
                分工名單
              </button>
              <button
                onClick={() => navigateTo('my-registration')}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${currentView === 'my-registration' ? 'text-blue-800 bg-blue-50 shadow-inner' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
              >
                查詢/取消
              </button>
            </div>

            <div className="flex shrink-0 items-center gap-1 md:gap-4">
              {!isAdmin && !canOpenInventory && (
                <button
                  onClick={() => navigateTo('admin-login')}
                  className="text-gray-400 hover:text-blue-600 transition-all p-2.5 rounded-full hover:bg-blue-50 flex items-center hover:scale-110"
                  title="後台與物資登入"
                >
                  <UserCog size={24} />
                </button>
              )}

              {canOpenInventory && (
                <>
                  <button
                    onClick={() => navigateTo('inventory-management')}
                    className={`flex h-10 w-10 items-center justify-center rounded-xl p-0 text-sm font-bold transition-all duration-200 btn-press md:h-auto md:w-auto md:px-4 md:py-2 ${currentView === 'inventory-management' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    title="物資管理"
                  >
                    <Package size={18} className="md:mr-2" />
                    <span className="hidden md:inline">物資管理</span>
                  </button>
                  {isAdmin && (
                  <button
                    onClick={() => navigateTo('admin-dashboard')}
                    className={`flex h-10 w-10 items-center justify-center rounded-xl p-0 text-sm font-bold transition-all duration-200 btn-press md:h-auto md:w-auto md:px-4 md:py-2 ${currentView === 'admin-dashboard' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    title="後台管理"
                  >
                    <LayoutDashboard size={18} className="md:mr-2" />
                    <span className="hidden md:inline">後台管理</span>
                  </button>
                  )}
                  <div className="ml-1 flex items-center border-l border-gray-200 pl-1 md:ml-2 md:pl-2">
                    {user && (
                      <div className="mr-2 hidden h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-blue-50 text-xs font-extrabold text-blue-700 shadow-sm md:flex">
                        <span aria-label="登入者頭像">{getUserInitials(user)}</span>
                      </div>
                    )}
                    <button
                      onClick={handleLogout}
                      className="flex h-10 w-10 items-center justify-center rounded-xl p-0 text-sm font-bold text-red-500 transition-all hover:bg-red-50 btn-press md:h-auto md:w-auto md:px-3 md:py-2"
                      title="登出"
                    >
                      <LogOut size={18} className="md:mr-2" />
                      <span className="hidden md:inline">登出</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative flex-grow py-6 md:py-8">
        {showGlobalLoadingOverlay && (
          <div className="fixed inset-0 bg-white/60 z-[60] flex items-center justify-center backdrop-blur-sm animate-fade-in">
            <Loader2 className="animate-spin text-blue-600 h-12 w-12" />
          </div>
        )}

        {currentView === 'home' && (
          <div className="container mx-auto px-4 animate-fade-in-up">
            <div className="mb-5 flex justify-center gap-3 md:hidden">
              <button
                onClick={() => navigateTo('public-roster')}
                className="flex min-w-0 items-center rounded-full border border-gray-200 bg-white px-5 py-3 text-sm font-bold text-gray-700 shadow-sm transition-all hover:shadow-md active:scale-95"
              >
                <Users size={16} className="mr-2" /> 分工名單
              </button>
              <button
                onClick={() => navigateTo('my-registration')}
                className="flex min-w-0 items-center rounded-full border border-gray-200 bg-white px-5 py-3 text-sm font-bold text-gray-700 shadow-sm transition-all hover:shadow-md active:scale-95"
              >
                <Search size={16} className="mr-2" /> 查詢/取消
              </button>
            </div>

            {!hasLoadedPublicData ? (
              <div className="min-h-[50vh] flex flex-col items-center justify-center text-center">
                <Loader2 className="animate-spin text-blue-600 h-10 w-10 mb-4" />
                <p className="text-gray-500 font-bold">梯隊資料載入中...</p>
              </div>
            ) : (
              <EventSchedule
                events={allEvents}
                year={systemYear}
                onRegisterClick={handleRegisterClick}
              />
            )}
          </div>
        )}

        {currentView === 'form' && (
          <ErrorBoundary fallback={
            <div className="container mx-auto px-4">
              <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500 animate-fade-in">
                <AlertTriangle size={48} className="mb-4 text-red-500" />
                <p className="text-xl font-bold">報名表單載入失敗</p>
                <p className="text-sm text-gray-400 mt-2">請重新整理頁面後再試</p>
              </div>
            </div>
          }>
            <div className="container mx-auto px-4 animate-fade-in-up">
              <div className="text-center mb-10">
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3 tracking-tight">{formYear} 醫宣義診梯隊報名</h1>
              </div>
              {!hasLoadedPublicData ? (
                <div className="min-h-[40vh] flex flex-col items-center justify-center text-center">
                  <Loader2 className="animate-spin text-blue-600 h-10 w-10 mb-4" />
                  <p className="text-gray-500 font-bold">梯隊資料載入中...</p>
                </div>
              ) : (
                <RegistrationForm
                  key={formYear}
                  events={formEvents}
                  year={formYear}
                  onSubmit={handleNewRegistration}
                  showToast={showToast}
                  preSelectedEventId={preSelectedEventId}
                />
              )}
            </div>
          </ErrorBoundary>
        )}

        {currentView === 'public-roster' && (
          <div className="animate-fade-in-up">
            <PublicRoster events={activeEvents} registrations={publicRegistrations} />
          </div>
        )}

        {currentView === 'my-registration' && (
          <MyRegistration events={allEvents} showToast={showToast} />
        )}

        {currentView === 'outlook-record-submit' && (
          <OutlookRecordSubmit
            recordId={outlookSubmitRecordId}
            user={user}
            showToast={showToast}
          />
        )}

        {currentView === 'admin-login' && (
          <div className="flex items-center justify-center min-h-[60vh] px-4 animate-fade-in-up">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl max-w-md w-full border border-gray-100 text-center transform transition-all hover:shadow-2xl">
              <div className="flex justify-center mb-6">
                <div className="bg-blue-50 p-5 rounded-full ring-8 ring-blue-50/50">
                  <ShieldCheck className="text-blue-600" size={48} />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">後台與物資登入</h2>
              <p className="text-gray-500 mb-8 text-sm">請使用授權的 Google 帳號登入</p>

              <button
                onClick={handleGoogleLogin}
                className="w-full bg-white border-2 border-gray-200 text-gray-700 font-bold py-4 rounded-xl hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-3 btn-press group"
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-base font-extrabold text-blue-600 shadow-sm group-hover:scale-110 transition-transform"
                  aria-hidden="true"
                >
                  G
                </span>
                使用 Google 帳號登入
              </button>

              <button
                onClick={() => {
                  setLoginError('');
                  navigateTo('inventory-access');
                }}
                className="mt-4 w-full rounded-xl py-3 text-sm font-bold text-blue-600 transition-colors hover:bg-blue-50"
              >
                使用梯次通行碼
              </button>

              {loginError && (
                <div className="mt-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm font-bold flex items-center justify-center animate-shake">
                  <AlertTriangle size={18} className="mr-2 flex-shrink-0" />
                  {loginError}
                </div>
              )}
            </div>
          </div>
        )}

        {currentView === 'inventory-access' && (
          <div className="flex items-center justify-center min-h-[60vh] px-4 animate-fade-in-up">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl max-w-md w-full border border-gray-100">
              <div className="flex justify-center mb-6">
                <div className="bg-blue-50 p-5 rounded-full ring-8 ring-blue-50/50">
                  <KeyRound className="text-blue-600" size={48} />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">現場物資清點</h2>
              <p className="text-gray-500 mb-8 text-sm text-center">
                輸入負責人提供的梯次通行碼，不需要 Google 帳號。
              </p>

              <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="inventory-access-name">
                您的姓名
              </label>
              <input
                id="inventory-access-name"
                value={accessCodeName}
                onChange={(event) => setAccessCodeName(event.target.value)}
                placeholder="請輸入姓名"
                autoComplete="name"
                className="mb-5 min-h-12 w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />

              <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="inventory-access-code">
                梯次通行碼
              </label>
              <input
                id="inventory-access-code"
                value={accessCodeInput}
                onChange={(event) => setAccessCodeInput(event.target.value.toUpperCase())}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleRedeemInventoryCode();
                }}
                placeholder="4 碼英數字"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                maxLength={8}
                className="mb-6 min-h-12 w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-center font-mono text-xl font-black tracking-[0.3em] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 placeholder:font-sans placeholder:text-base placeholder:font-normal placeholder:tracking-normal"
              />

              <button
                onClick={handleRedeemInventoryCode}
                disabled={isRedeemingCode}
                className="w-full rounded-xl bg-blue-600 py-4 font-bold text-white transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300 btn-press flex items-center justify-center gap-2"
              >
                {isRedeemingCode && <Loader2 size={18} className="animate-spin" />}
                {isRedeemingCode ? '驗證中…' : '進入物資清點'}
              </button>

              <button
                onClick={() => {
                  setLoginError('');
                  navigateTo('admin-login');
                }}
                className="mt-4 w-full rounded-xl py-3 text-sm font-bold text-gray-500 transition-colors hover:bg-gray-50"
              >
                我有 Google 管理帳號
              </button>

              {loginError && (
                <div className="mt-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm font-bold flex items-center justify-center animate-shake">
                  <AlertTriangle size={18} className="mr-2 flex-shrink-0" />
                  {loginError}
                </div>
              )}
            </div>
          </div>
        )}

        {currentView === 'inventory-management' && (
          canOpenInventory ? (
            <ErrorBoundary fallback={
              <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500 animate-fade-in">
                <AlertTriangle size={48} className="mb-4 text-red-500" />
                <p className="text-xl font-bold">物資管理載入失敗</p>
                <p className="text-sm text-gray-400 mt-2">請重新整理頁面或聯繫系統管理員</p>
              </div>
            }>
              <div className="container mx-auto px-4 animate-fade-in">
                <InventoryManager
                  events={activeEvents}
                  systemYear={systemYear}
                  currentUserLabel={currentUserLabel}
                  showToast={showToast}
                  lockedEventId={inventoryCodeSession?.eventId ?? null}
                  canManageAccessCodes={isAdmin}
                />
              </div>
            </ErrorBoundary>
          ) : (
            <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500 animate-fade-in">
              <AlertTriangle size={48} className="mb-4 text-yellow-500" />
              <p className="text-xl font-bold">請先登入物資管理帳號</p>
              <button
                onClick={() => navigateTo('admin-login')}
                className="mt-5 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700"
              >
                前往登入
              </button>
            </div>
          )
        )}

        {currentView === 'admin-dashboard' && (
          isAdmin ? (
            <ErrorBoundary fallback={
              <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500 animate-fade-in">
                <AlertTriangle size={48} className="mb-4 text-red-500" />
                <p className="text-xl font-bold">儀表板載入失敗</p>
                <p className="text-sm text-gray-400 mt-2">請重新整理頁面或聯繫技術支援</p>
              </div>
            }>
              <div className="animate-fade-in">
                <Dashboard
                  registrations={registrations}
                  historicalData={historicalData}
                  tripParticipants={tripParticipants}
                  watchlist={watchlist}
                  events={allEvents}
                  isEventDataLoading={!hasLoadedPublicData}
                  systemYear={systemYear}
                  onUpdateSystemYear={handleUpdateSystemYear}
                  onUpdateHistoricalData={handleUpdateHistorical}
                  onUpdateWatchlist={handleUpdateWatchlist}
                  onUpdateEvents={handleAddEvents}
                  onEditEvent={handleEditEvent}
                  onUpdateEventAndRegistrants={handleUpdateEventAndRegistrants}
                  onCreateRegistrant={handleCreateRegistrant}
                  onUpdateRegistrant={handleUpdateRegistrant}
                  currentUserLabel={currentUserLabel}
                  showToast={showToast}
                />
              </div>
            </ErrorBoundary>
          ) : (
            <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500 animate-fade-in">
              <AlertTriangle size={48} className="mb-4 text-yellow-500" />
              <p className="text-xl font-bold">權限不足或未登入</p>
            </div>
          )
        )}
      </main>

      <footer className="bg-white border-t border-gray-100 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-400 font-medium">
            &copy; {systemYear} True Jesus Church Medical Missionary Team.
          </p>
        </div>
      </footer>
      </div>
    </ErrorBoundary>
  );
};

export default App;
